import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaGoogle, FaEnvelope, FaLock } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';
import './Login.css';

const Login = () => {
  const { login, loginWithGoogle, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get the redirect path from location state or default to home
  const from = location.state?.from || '/';

  // Display auth errors from context
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate(from);
    } catch (err) {
      setError('Failed to sign in. Please check your credentials.');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
    } catch (err) {
      setError('Failed to sign in with Google.');
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Login - Student Helper"
        description="Sign in to your Student Helper account to access study materials and Q&A features."
        keywords="student helper login, sign in, user authentication, student account"
      />
      <div className="login-container">
        <div className="wave-background"></div>
        <div className="circle-blob"></div>
        
        <motion.div 
          className="login-split-layout"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="login-left">
            {/* Left side empty for design aesthetic */}
          </div>
          
          <motion.div 
            className="login-form-section"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="login-card">
              <div className="login-header">
                <div className="login-logo">
                  <span>Welcome</span>
                </div>
                <h2>Sign In</h2>
                <p className="login-subtitle">Access your Student Helper account</p>
              </div>
              
              {error && (
                <motion.div 
                  className="alert alert-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}
              
              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="login-input"
                  />
                </div>
                <div className="form-group">
                  <FaLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="login-input"
                  />
                </div>
                
                <div className="login-links">
                  <div className="remember-me">
                    <input type="checkbox" id="remember" />
                    <label htmlFor="remember">Remember me</label>
                  </div>
                  <Link to="/forgot-password" className="forgot-password">
                    Forgot Password?
                  </Link>
                </div>
                
                <motion.button 
                  type="submit" 
                  disabled={loading} 
                  className="login-button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? "Signing in..." : "Login"}
                </motion.button>
              </form>
              
              <div className="or-divider">
                <span>or</span>
              </div>
              
              <motion.button 
                onClick={handleGoogleSignIn} 
                disabled={loading} 
                className="google-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaGoogle className="google-icon" />
                Continue with Google
              </motion.button>
              
              <p className="register-prompt">
                New to Student Helper?
                <Link to="/register" className="register-link">
                  Create Account
                </Link>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default Login; 